import { useState, useEffect, createContext, useContext } from 'react'
import { supabase } from '../supabase'
import Median from 'median-js-bridge'

const AuthContext = createContext(null)

// Reviewer / test-account backdoor. See supabase/functions/dev-signin.
// Only this exact pair is honored — every other email + code combo falls
// through to real Supabase OTP. Honored both in the Auth.jsx flows below
// and in the OnboardingFlow email step (which imports these). Removing it:
// delete these two constants, the `=== DEV_BYPASS_*` checks that use them,
// and the dev-signin function.
export const DEV_BYPASS_EMAIL = 'leaberkley+google@gmail.com'
export const DEV_BYPASS_CODE  = '123456'

// Defensive check - invitations.church_id and profiles.church_id are uuid
// columns; never pass a slug or arbitrary string through to them.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
function asUuid(v) {
  if (typeof v === 'string' && UUID_RE.test(v.trim())) return v.trim()
  return null
}

// Magic-link callback target. Localhost dev returns to localhost:3000;
// everything else returns to the production domain.
function authRedirectUrl() {
  if (typeof window === 'undefined') return 'https://thebiblecommunity.co.uk/login'
  return window.location.hostname === 'localhost'
    ? 'http://localhost:3000'
    : 'https://thebiblecommunity.co.uk/login'
}

export function AuthProvider({ children }) {
  const [user,     setUser]     = useState(null)
  const [profile,  setProfile]  = useState(null)
  const [church,   setChurch]   = useState(null)
  const [churches, setChurches] = useState([])
  const [loading,  setLoading]  = useState(true)
  // Surfaced to the sign-in screens so auth-callback / profile-setup failures
  // (e.g. after Apple/Google OAuth) show on screen, not just the console.
  const [authError, setAuthError] = useState(null)
  function clearAuthError() { setAuthError(null) }

  // Pull (or re-pull) the churches list. Exposed via the context so any
  // component that mutates a church row can call this and have the rest of
  // the app pick up the change without a page reload.
  async function refreshChurches() {
    const { data, error } = await supabase.from('churches').select('*').order('name')
    if (error) { console.error('useAuth churches load:', error); return { error } }
    setChurches(data || [])
    return { data }
  }

  useEffect(() => {
    // Don't filter by active - older schemas may have NULL there, which
    // .eq('active', true) silently excludes. We'd rather show the church.
    refreshChurches()

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null)
      if (session?.user) fetchProfile(session.user)
      else { setProfile(null); setChurch(null); setLoading(false) }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(authUser) {
    const userId = authUser?.id
    if (!userId) { setLoading(false); return }

    let { data: prof } = await supabase.from('profiles').select('*').eq('id', userId).single()

    // Self-heal: the DB trigger that creates a profile on signup can fail
    // (e.g. for Google OAuth users when metadata is unexpected). If we end up
    // signed in with no profile row, insert a minimal visitor profile so the
    // rest of the app has something to read.
    if (!prof) {
      const meta = authUser.user_metadata || {}
      const fallbackName = meta.full_name || meta.name || null
      const { data: created, error: insertErr } = await supabase
        .from('profiles')
        .insert({
          id: userId,
          full_name: fallbackName,
          email: authUser.email,
          role: 'visitor',
          church_id: null,
          onboarding_complete: false,
        })
        .select()
        .single()
      if (created) {
        prof = created
      } else if (insertErr && (insertErr.code === '23505' || /duplicate key|profiles_pkey/i.test(insertErr.message || ''))) {
        // A profile already exists — the signup trigger created it (with the
        // invited role + church) but our initial read missed it, typically
        // read-replica lag right after the write. So DON'T treat the duplicate
        // as a failure: re-read the real row (retrying briefly for the replica
        // to catch up) instead of blocking the invited user at "couldn't finish
        // setting up your account".
        for (let i = 0; i < 5 && !prof; i++) {
          await new Promise(r => setTimeout(r, 300))
          const { data: again } = await supabase.from('profiles').select('*').eq('id', userId).maybeSingle()
          if (again) prof = again
        }
        if (!prof) {
          setAuthError("We couldn't load your account just now. Please close the app and open it again.")
        }
      } else if (insertErr) {
        console.error('useAuth profile self-heal failed:', insertErr)
        const m = `We couldn't finish setting up your account. ${insertErr.message || insertErr.hint || insertErr.code || ''}`.trim()
        setAuthError(m)
      }
    }

    // We have a usable profile — clear any prior auth/setup error.
    if (prof) setAuthError(null)

    // Google sign-in users come with an avatar URL in user_metadata. If we
    // have a profile but no avatar_url stored, copy theirs across on first
    // login so the rest of the app picks it up without an extra upload step.
    if (prof && !prof.avatar_url) {
      const meta = authUser.user_metadata || {}
      const googleAvatar = meta.avatar_url || meta.picture || null
      if (googleAvatar) {
        const { data: updated, error: avatarErr } = await supabase
          .from('profiles')
          .update({ avatar_url: googleAvatar })
          .eq('id', userId)
          .select()
          .single()
        if (avatarErr) console.warn('useAuth Google avatar sync failed:', avatarErr)
        else prof = updated
      }
    }

    // Invited, but signed in SEPARATELY (not via the invite link): if a pending
    // invitation exists for this user's email, apply the invited role + church
    // so they still get the access they were granted — same outcome as clicking
    // the link. Only touches users who aren't set up yet (a visitor, or no
    // church), so it never disturbs or downgrades an established member. The
    // church-id check keeps it idempotent even if marking the invite accepted
    // is denied by RLS.
    if (prof && authUser.email && (prof.role === 'visitor' || !prof.church_id)) {
      try {
        // get_my_pending_invitation() matches by the caller's own verified JWT
        // email server-side (not a client-supplied value) — see
        // supabase-schema-security-invitations.sql. accept_invitation() then
        // applies role/church_id to auth.uid() and marks it accepted.
        const { data: rows } = await supabase.rpc('get_my_pending_invitation')
        const pending = Array.isArray(rows) ? rows[0] : rows
        if (pending?.token) {
          const { error: acceptErr } = await supabase.rpc('accept_invitation', { p_token: pending.token })
          if (!acceptErr) {
            const { data: updated } = await supabase.from('profiles').select('*').eq('id', userId).single()
            if (updated) prof = updated
          }
        }
      } catch (e) { console.warn('useAuth: invite auto-apply failed:', e) }
    }

    setProfile(prof)
    if (prof?.church_id) {
      const { data: ch } = await supabase.from('churches').select('*').eq('id', prof.church_id).single()
      setChurch(ch)
    }
    setLoading(false)
  }

  // Re-pull the current user's profile into context. Used after a flow that
  // writes the profile row directly (keyed by a known user id) rather than
  // through updateProfile — e.g. the invite screen finalising avatar +
  // onboarding_complete right after the OTP is verified.
  async function refreshProfile() {
    const { data: { session } } = await supabase.auth.getSession()
    if (session?.user) await fetchProfile(session.user)
  }

  async function signInWithEmail(email) {
    // For the bypass email, skip the real OTP send and pretend success so the
    // UI advances to the code-entry screen where verifyEmailOtp does the work.
    if (String(email || '').trim().toLowerCase() === DEV_BYPASS_EMAIL) {
      return { error: null }
    }
    const { error } = await supabase.auth.signInWithOtp({
      email,
      options: {
        emailRedirectTo: authRedirectUrl(),
        // Sign-in only: never auto-create a profile from this entry point.
        // New users come in via signUpAsVisitor / signUpFromInvite, which
        // attach role + church metadata at creation time.
        shouldCreateUser: false,
      },
    })
    return { error }
  }

  // Verify a 6-digit code that Supabase emails alongside the magic link.
  // Lets users sign in directly inside the PWA (or any browser tab) without
  // clicking the link — important because magic links open in the system
  // browser on iOS, leaving the installed PWA without a session.
  // Requires the Supabase "Magic Link" email template to contain {{ .Token }}.
  async function verifyEmailOtp(email, token) {
    const trimmedToken = String(token || '').trim()
    const trimmedEmail = String(email || '').trim().toLowerCase()

    // Bypass: dev-signin server-side mints a magic-link token_hash that we
    // exchange for a real session here, so the rest of the app sees a normal
    // signed-in user.
    if (trimmedEmail === DEV_BYPASS_EMAIL && trimmedToken === DEV_BYPASS_CODE) {
      const { data: devData, error: devErr } = await supabase.functions.invoke('dev-signin', {
        body: { email: trimmedEmail, code: trimmedToken },
      })
      if (devErr) return { data: null, error: devErr }
      if (!devData?.token_hash) {
        return { data: null, error: { message: 'dev-signin returned no token_hash' } }
      }
      const { data, error } = await supabase.auth.verifyOtp({
        type: 'magiclink',
        token_hash: devData.token_hash,
      })
      return { data, error }
    }

    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: trimmedToken,
      type:  'email',
    })
    return { data, error }
  }

  // Shared by signUpFromInvite (email+OTP path) and applyInviteToExistingUser
  // (OAuth path, below) — look up the invitation by token ONLY. We
  // deliberately don't reject on expiry or a prior `accepted` flag: church
  // invites are admin-trusted and long-lived, and the one-time code emailed
  // to the invited address (or, for OAuth, the already-authenticated session)
  // is the real gate. This fixes weeks-old invites — and retries where a
  // previous attempt already flipped `accepted` — showing "invalid".
  // get_invitation(p_token) is a SECURITY DEFINER RPC (see
  // supabase-schema-security-invitations.sql) — the raw `invitations` table
  // no longer has a public SELECT policy, so every lookup goes through this.
  async function lookupValidInvite(token) {
    const { data } = await supabase.rpc('get_invitation', { p_token: token })
    const invite = Array.isArray(data) ? data[0] : data
    if (!invite) return { error: { message: 'This invitation link is not valid. Please ask your church for a fresh one.' } }
    const inviteChurchId = asUuid(invite.church_id)
    if (!inviteChurchId) return { error: { message: 'This invitation is missing a valid church reference. Please ask your church for a fresh invite.' } }
    return { invite, inviteChurchId }
  }

  // Apply an invitation's role + church to the CALLING user — used for the
  // OAuth-invite path (App.js, after the OAuth redirect round-trip
  // completes) where we already have a live session. accept_invitation()
  // always acts on auth.uid() server-side, never a client-supplied id, so
  // the `userId` param is kept only for the call site's own clarity/logging.
  async function applyInviteToExistingUser(token) {
    const { error } = await supabase.rpc('accept_invitation', { p_token: token })
    return { error }
  }

  async function signUpFromInvite(token, fullName) {
    const { invite, error: lookupErr } = await lookupValidInvite(token)
    if (lookupErr) return { error: lookupErr }

    // If a profile already exists for this email (e.g. they signed up as a
    // visitor earlier), email them a sign-in code rather than forcing a
    // brand-new magic-link signup. The role/church upgrade itself happens
    // AFTER verifyEmailOtp succeeds (Auth.jsx's handleVerifyCode calls
    // accept_invitation then) — not here, while the caller is still
    // unauthenticated and we can't yet prove they ARE that email's owner.
    const { data: existing, error: profileLookupErr } = await supabase
      .from('profiles')
      .select('id, role')
      .eq('email', invite.email)
      .maybeSingle()

    if (!profileLookupErr && existing?.id) {
      const { error: otpErr } = await supabase.auth.signInWithOtp({
        email: invite.email,
        options: { emailRedirectTo: authRedirectUrl(), shouldCreateUser: false },
      })
      return { error: otpErr, upgraded: true, email: invite.email, inviteToken: token }
    }

    // No matching profile - email a 6-digit sign-in code (Supabase OTP).
    // role/church_id no longer travel in signup metadata — Part 1's
    // handle_new_user() only trusts 'visitor'/'member' there regardless, and
    // accept_invitation() (called post-verification) applies the real invite.
    const { error } = await supabase.auth.signInWithOtp({
      email: invite.email,
      options: {
        emailRedirectTo: authRedirectUrl(),
        data: { full_name: fullName },
      }
    })
    return { error, email: invite.email, inviteToken: token }
  }

  // Sign up a visitor - no invite required. They get role='visitor' and
  // (optionally) a full name stashed in user_metadata so the profile-creation
  // trigger (and the self-heal path in fetchProfile) can populate it. Church
  // selection happens after auth via the post-auth ChurchPicker screen.
  async function signUpAsVisitor(email, fullName) {
    if (!email?.trim()) return { error: { message: 'Email is required.' } }
    const { error } = await supabase.auth.signInWithOtp({
      email: email.trim(),
      options: {
        emailRedirectTo: authRedirectUrl(),
        data: { role: 'visitor', full_name: fullName?.trim() || null },
      },
    })
    return { error }
  }

  // Visitor → church request for elevated access. `feature` is a label like
  // "community" / "comment" / "question" / "encouragement". Returns the row
  // (or the existing one if the visitor has already asked for this feature).
  // Falls back to a feature-less upsert if the access_requests table hasn't
  // had the `feature` column migration applied yet.
  async function requestAccess(feature, churchId, message) {
    if (!user) return { error: { message: 'You need to be signed in.' } }
    const targetChurchId = churchId || profile?.church_id || null
    if (!targetChurchId) return { error: { message: 'No church selected - choose one in your profile first.' } }

    const baseRow = {
      user_id:   user.id,
      church_id: targetChurchId,
      message:   message || null,
      status:    'pending',
    }

    let { data, error } = await supabase.from('access_requests')
      .upsert(
        { ...baseRow, feature: feature || null },
        { onConflict: 'user_id,church_id,feature' }
      )
      .select().single()

    // Schema-cache fallback: older deployments don't have a `feature` column
    // (or its unique index), so retry without it.
    if (error && /feature/i.test(error.message || '') && /column|schema/i.test(error.message || '')) {
      console.warn('access_requests.feature missing - retrying without it.')
      ;({ data, error } = await supabase.from('access_requests')
        .upsert(baseRow, { onConflict: 'user_id,church_id' })
        .select().single())
    }

    return { data, error }
  }

  async function signOut() {
    // Clear the legacy unscoped per-account preferences. `nlc.activeChurchId`
    // and `nlc.sermonNoteChurchIds` are now scoped by user.id (see
    // userScopedKey/readUserScoped in BibleApp.jsx) so they no longer need
    // wiping — the next account simply reads its own scoped copy.
    try {
      localStorage.removeItem('nlc.lastPlace')
      localStorage.removeItem('nlc.sermonLegendDismissed')
      localStorage.removeItem('walkthroughComplete')
      localStorage.removeItem('nlc.onboardingDone')
    } catch (_e) { /* ignore quota / SSR */ }
    // Unlink this device from the OneSignal external id so the next account on
    // the same device doesn't receive this user's pushes (Median native only).
    try {
      if (Median.isNativeApp && Median.isNativeApp()) Median.onesignal.externalUserId.remove()
    } catch (_e) { /* web / bridge not present */ }
    await supabase.auth.signOut()
  }

  // Permanently delete the current user's account via the delete-account
  // edge function. Returns { ok: true } on success or { error } on
  // failure. The edge function uses the service-role key to drop the
  // auth.users row + cascade to profile + user-owned content.
  async function deleteAccount() {
    if (!user?.id) return { error: { message: 'Not signed in.' } }
    const { data: sessionData } = await supabase.auth.getSession()
    const accessToken = sessionData?.session?.access_token
    if (!accessToken) return { error: { message: 'No active session.' } }
    const url = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/delete-account`
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type':  'application/json',
          'apikey':        process.env.REACT_APP_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({}),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || data?.error) {
        return { error: { message: data?.error || `HTTP ${res.status}` } }
      }
      // Wipe local sign-in state. The auth.users row is already gone so
      // signOut just clears the client-side session storage.
      await signOut()
      return { ok: true }
    } catch (e) {
      return { error: { message: e?.message || String(e) } }
    }
  }

  async function updateProfile(updates) {
    const { data, error } = await supabase.from('profiles').update(updates).eq('id', user.id).select().single()
    if (!error) {
      setProfile(data)
      // If church_id changed, refresh the active church object too so any
      // consumer reading useAuth().church (name, location, slug, website)
      // sees the new church immediately rather than the stale one from
      // sign-in time.
      if (Object.prototype.hasOwnProperty.call(updates, 'church_id')) {
        if (data.church_id) {
          const { data: ch } = await supabase.from('churches').select('*').eq('id', data.church_id).single()
          setChurch(ch || null)
        } else {
          setChurch(null)
        }
      }
    }
    return { error }
  }

  async function sendInvite(emailOrPhone, role, churchId) {
    const isEmail = emailOrPhone.includes('@')
    // Always store a UUID in invitations.church_id. Accept the caller's value
    // only if it parses as a uuid; if it's a slug or anything else, try to
    // resolve it from the loaded `churches` list before falling back to the
    // current user's church.
    let targetChurchId = asUuid(churchId)
    if (!targetChurchId && typeof churchId === 'string' && churchId.trim()) {
      const match = churches.find(c => c.slug === churchId.trim())
      if (match) targetChurchId = asUuid(match.id)
    }
    if (!targetChurchId) targetChurchId = asUuid(profile?.church_id)
    if (!targetChurchId) return { error: { message: 'Please choose a church.' } }

    const { data, error } = await supabase.from('invitations').insert({
      church_id: targetChurchId, invited_by: user.id,
      email: isEmail ? emailOrPhone : null,
      phone: isEmail ? null : emailOrPhone, role,
    }).select().single()
    if (error) return { data: null, error }

    // Build a role-specific WhatsApp-style invite message the caller can
    // copy/share. URL is the production domain regardless of where the
    // invite was created — magic links land on prod. Church name comes
    // from a fresh DB lookup (rather than the local `churches` array)
    // so super-admins inviting into another church get the right name.
    let churchName = churches.find(c => c.id === targetChurchId)?.name || 'your church'
    try {
      const { data: chData } = await supabase.from('churches')
        .select('name').eq('id', targetChurchId).single()
      if (chData?.name) churchName = chData.name
    } catch { /* fall back to the local value */ }
    const inviteUrl  = `https://thebiblecommunity.co.uk/?invite=${data.token}`
    const messages = {
      elder: `You've been invited as an Elder at ${churchName} on The Bible Community

Your Bible, enriched with your church's sermon notes and video links. Connect with your congregation through prayer, encouragement and community reflection.

Join here: ${inviteUrl}`,
      admin: `You've been invited as an admin at ${churchName} on The Bible Community

Your Bible, enriched with your church's sermon notes and video links. As an admin you can manage your church community, invite members and upload sermon content.

Join here: ${inviteUrl}`,
      member: `You've been invited to join ${churchName} on The Bible Community

Read your Bible with your church's sermon notes and video links on every verse. Share reflections, post prayer requests and stay connected with your congregation all week.

Join here: ${inviteUrl}`,
    }
    const message = messages[role] || messages.member

    // Best-effort email delivery via Resend through the Edge Function.
    // The invite row already exists, so a failed email isn't fatal — the
    // caller can still copy/share the link from the response.
    let emailSent = false
    let emailError = null
    if (isEmail) {
      try {
        const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY
        console.log('[invite] POST send-invite-email →', { to: emailOrPhone, role, churchName })
        const res = await fetch(
          'https://icsdpckobituqivafuqo.supabase.co/functions/v1/send-invite-email',
          {
            method: 'POST',
            headers: {
              'Content-Type':  'application/json',
              'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
              'apikey':        SUPABASE_ANON_KEY,
            },
            body: JSON.stringify({
              email:       emailOrPhone,
              role,
              churchName,
              inviteUrl,
              inviterName: profile?.full_name || profile?.email || '',
            }),
          }
        )
        console.log('[invite] send-invite-email status', res.status)
        if (res.ok) emailSent = true
        else {
          const body = await res.json().catch(() => ({}))
          emailError = body?.error || `Email send failed (${res.status})`
          console.warn('[invite] send-invite-email error:', res.status, body)
        }
      } catch (e) {
        emailError = e?.message || String(e)
        console.warn('[invite] send-invite-email exception:', e)
      }
    }

    return { data, error: null, inviteUrl, message, churchName, emailSent, emailError }
  }

  // Slug helper — lowercase, non-alphanum → '-', strip leading/trailing dashes.
  function nameToSlug(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '')
  }

  // Create a church. Only `name` is required. `adminEmail` is optional — if
  // empty/missing, no invite is generated and the super admin can invite the
  // first admin later from the invite panel. After insert, the super admin's
  // own profile.church_id is updated so they immediately operate on the new
  // church.
  async function createChurch(name, location, website, adminEmail) {
    if (profile?.role !== 'super_admin') {
      console.warn('[createChurch] denied — caller is not super_admin')
      return { error: { message: 'Only super admins can create churches.' } }
    }
    const cleanName = String(name || '').trim()
    if (!cleanName) return { error: { message: 'Church name is required.' } }

    const slug = nameToSlug(cleanName)
    console.log('[createChurch] step 1/4 — inserting church:', { name: cleanName, slug, location, website })

    const payload = { name: cleanName, slug }
    if (location && String(location).trim()) payload.location = String(location).trim()
    if (website  && String(website).trim())  payload.website  = String(website).trim()

    const { data: newChurch, error: churchErr } = await supabase
      .from('churches').insert(payload).select().single()
    if (churchErr) {
      console.error('[createChurch] church insert failed:', churchErr)
      return { error: { message: churchErr.message || 'Could not create church.' } }
    }
    console.log('[createChurch] step 2/4 — church created:', newChurch)

    // Adopt the newly-created church as the super admin's home only when
    // they don't have one yet — otherwise we'd silently swap their
    // existing home, which (a) confuses the admin and (b) demotes the
    // previous home to a "followed" church via the activeChurchId →
    // sermonNoteChurchIds prepend + DB sync. The admin can still switch
    // to the new church from the church picker if they want to.
    if (user?.id && !profile?.church_id) {
      console.log('[createChurch] step 3/4 — adopting new church as home (was unset)')
      const { error: profErr } = await supabase
        .from('profiles').update({ church_id: newChurch.id }).eq('id', user.id)
      if (profErr) {
        console.warn('[createChurch] profile update failed (non-fatal):', profErr)
      } else {
        setProfile(p => p ? { ...p, church_id: newChurch.id } : p)
        setChurch(newChurch)
      }
    } else {
      console.log('[createChurch] step 3/4 — leaving existing home church intact')
    }

    // Refresh the churches list so other parts of the UI see the new church.
    const { data: refreshed } = await supabase.from('churches').select('*').order('name')
    if (Array.isArray(refreshed)) setChurches(refreshed)

    // Optional invite for the first admin.
    let invite = null
    let inviteError = null
    const cleanEmail = String(adminEmail || '').trim()
    if (cleanEmail) {
      console.log('[createChurch] step 4/4 — sending admin invite to', cleanEmail)
      const { data: inv, error: inviteErr } = await supabase
        .from('invitations')
        .insert({ church_id: newChurch.id, invited_by: user.id, email: cleanEmail, role: 'admin' })
        .select().single()
      if (inviteErr) {
        console.warn('[createChurch] invite insert failed (non-fatal):', inviteErr)
        inviteError = inviteErr.message || 'Invite could not be saved.'
      } else {
        invite = inv
        console.log('[createChurch] invite created:', inv.id)
      }
    } else {
      console.log('[createChurch] step 4/4 — no admin email supplied, skipping invite')
    }

    return { church: newChurch, invite, error: null, inviteError }
  }

  const isElder      = ['elder','admin','super_admin'].includes(profile?.role)
  const isAdmin      = ['admin','super_admin'].includes(profile?.role)
  const isSuperAdmin = profile?.role === 'super_admin'
  const isVisitor    = profile?.role === 'visitor'
  const canModerate  = isElder

  return (
    <AuthContext.Provider value={{
      user, profile, church, churches, loading, authError, clearAuthError,
      signInWithEmail, verifyEmailOtp, signUpFromInvite, applyInviteToExistingUser, signUpAsVisitor, signOut, deleteAccount, updateProfile, refreshProfile,
      sendInvite, createChurch, refreshChurches, requestAccess,
      isElder, isAdmin, isSuperAdmin, isVisitor, canModerate,
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
